import { Pressable, StyleSheet, Text } from "react-native";

import { webTapFloor, type UiTheme } from "../theme";

// THE show/hide control for a picker's filter axes.
//
// It exists because the add picker grew one and the swap picker never got it:
// the same screen-shape, one with collapsible filters and one with two chip
// rows permanently eating the top of the list. Antenore found it from the
// device, and he was right that it was a miss at implementation time, not a
// decision.
//
// The count rides in the LABEL, not only in the fill: a filter that is
// narrowing the list has to stay readable while the rows that set it are
// collapsed, and that also keeps the active state from being colour-only.
//
// ⚠️ Known residual, registered in next/consolidation.md: the box values here
// echo `pickerStyles.chip*`, which cannot move because five other surfaces in
// the picker family read those keys. The end state is the house <Chip> once it
// grows the trailing-affordance slot its own comment defers; until then this
// is one control in one place instead of a third hand-rolled pill.
export type FiltersToggleProps = {
  theme: UiTheme;
  open: boolean;
  onToggle: () => void;
  /** How many filters are currently narrowing the list. */
  activeCount: number;
  t: (k: string, opts?: Record<string, unknown>) => string;
};

export function FiltersToggle({
  theme,
  open,
  onToggle,
  activeCount,
  t,
}: FiltersToggleProps) {
  const styles = makeStyles(theme);
  const active = activeCount > 0;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ expanded: open }}
      onPress={onToggle}
      hitSlop={{ top: 4, bottom: 4 }}
      style={({ pressed }) => [
        styles.chip,
        active && styles.chipActive,
        pressed && styles.chipPressed,
      ]}
    >
      <Text style={[styles.chipText, active && styles.chipTextActive]}>
        {active
          ? t("mobile.logger.pickerFiltersN", {
              n: activeCount,
              defaultValue: "Filters ({{n}})",
            })
          : t("mobile.logger.pickerFilters", { defaultValue: "Filters" })}
        {open ? " ▴" : " ▾"}
      </Text>
    </Pressable>
  );
}

const makeStyles = (theme: UiTheme) => {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    chip: {
      paddingHorizontal: s.s4,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
      minHeight: webTapFloor ?? 36,
      justifyContent: "center",
    },
    chipPressed: { opacity: 0.7 },
    chipActive: { backgroundColor: p.brand500, borderColor: p.brand500 },
    chipText: { color: p.ink2, fontSize: ty.sizes.sm, fontWeight: "600" },
    chipTextActive: { color: p.inkOnBrand },
  });
};
